import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FAQ from '@/components/FAQ';
import ProductCarousel from '@/components/ProductCarousel';
import { categories } from '@/mocks/categories';
import { productsByCategory } from '@/mocks/products';

export default function Home() {
  const { lang = 'en' } = useParams<{ lang: string }>();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (lang && ['en', 'fr', 'da', 'de'].includes(lang)) {
      i18n.changeLanguage(lang);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [lang, i18n]);

  const resourceCards = [
    { title: t('card_spreadsheet_title'), desc: t('card_spreadsheet_desc'), icon: 'ri-file-list-3-line', color: 'bg-emerald-50 text-emerald-700', img: 'https://readdy.ai/api/search-image?query=Minimalist%20spreadsheet%20grid%20document%20layout%20clean%20white%20desk%20with%20laptop%20and%20coffee%20warm%20natural%20light%20fashion%20products%20organized%20flat%20lay%20editorial%20photography%20soft%20shadows%20professional%20style&width=400&height=300&seq=res-spreadsheet-01&orientation=landscape' },
    { title: t('card_shipping_title'), desc: t('card_shipping_desc'), icon: 'ri-truck-line', color: 'bg-amber-50 text-amber-700', img: 'https://readdy.ai/api/search-image?query=Cardboard%20shipping%20boxes%20stacked%20with%20shipping%20labels%20and%20tape%20on%20clean%20white%20background%20minimalist%20aesthetic%20warm%20neutral%20tones%20editorial%20product%20photography%20soft%20natural%20lighting%20professional%20arrangement&width=400&height=300&seq=res-shipping-02&orientation=landscape' },
    { title: t('card_qc_title'), desc: t('card_qc_desc'), icon: 'ri-shield-check-line', color: 'bg-teal-50 text-teal-700', img: 'https://readdy.ai/api/search-image?query=Quality%20control%20inspection%20hands%20checking%20fabric%20and%20clothing%20details%20clean%20white%20background%20magnifying%20glass%20minimalist%20style%20warm%20lighting%20professional%20editorial%20photography%20product%20focus&width=400&height=300&seq=res-qc-03&orientation=landscape' },
    { title: t('card_sellers_title'), desc: t('card_sellers_desc'), icon: 'ri-store-3-line', color: 'bg-rose-50 text-rose-700', img: 'https://readdy.ai/api/search-image?query=Small%20boutique%20storefront%20display%20with%20fashion%20items%20hanging%20minimalist%20interior%20warm%20natural%20light%20clean%20aesthetic%20editorial%20photography%20soft%20shadows%20professional%20style&width=400&height=300&seq=res-sellers-04&orientation=landscape' },
    { title: t('card_reviews_title'), desc: t('card_reviews_desc'), icon: 'ri-star-line', color: 'bg-orange-50 text-orange-700', img: 'https://readdy.ai/api/search-image?query=Five%20gold%20stars%20rating%20concept%20on%20clean%20white%20background%20minimalist%20style%20warm%20lighting%20customer%20review%20aesthetic%20editorial%20photography%20professional%20soft%20shadows&width=400&height=300&seq=res-reviews-05&orientation=landscape' },
    { title: t('card_safety_title'), desc: t('card_safety_desc'), icon: 'ri-lock-line', color: 'bg-slate-50 text-slate-700', img: 'https://readdy.ai/api/search-image?query=Modern%20security%20shield%20lock%20icon%20concept%20abstract%20minimalist%20design%20clean%20white%20background%20soft%20green%20accents%20digital%20protection%20editorial%20illustration%20professional%20warm%20tones&width=400&height=300&seq=res-safety-06&orientation=landscape' },
  ];

  // 10 Pillar Articles
  const pillarArticles = [
    { title: 'AllChinaBuy Spreadsheet Complete Guide', slug: 'allchinabuy-spreadsheet-complete-guide', desc: 'The ultimate guide to using AllChinaBuy spreadsheets for product research, seller evaluation, and smarter buying decisions.', img: 'https://readdy.ai/api/search-image?query=Flat%20lay%20fashion%20spreadsheet%20document%20with%20sneakers%20clothing%20accessories%20organized%20grid%20clean%20white%20background%20warm%20natural%20light%20editorial%20product%20photography%20professional%20style&width=600&height=400&seq=pillar-01&orientation=landscape' },
    { title: 'AllChinaBuy Shipping Cost Guide', slug: 'allchinabuy-shipping-guide', desc: 'Understand shipping methods, calculate costs, and learn how to reduce your international delivery expenses effectively.', img: 'https://readdy.ai/api/search-image?query=World%20map%20with%20shipping%20routes%20and%20parcel%20boxes%20minimalist%20illustration%20clean%20white%20background%20warm%20neutral%20tones%20editorial%20style%20professional%20soft%20shadows%20global%20logistics&width=600&height=400&seq=pillar-02&orientation=landscape' },
    { title: 'AllChinaBuy Review 2026', slug: 'allchinabuy-review', desc: 'An honest independent review of AllChinaBuy platform covering user experience, product quality, shipping speed, and overall reliability.', img: 'https://readdy.ai/api/search-image?query=Customer%20review%20concept%20with%20notebook%20pen%20and%20star%20ratings%20on%20clean%20white%20desk%20minimalist%20style%20warm%20lighting%20editorial%20photography%20professional%20soft%20shadows%20aesthetic&width=600&height=400&seq=pillar-03&orientation=landscape' },
    { title: 'AllChinaBuy QC Guide', slug: 'allchinabuy-qc-guide', desc: 'Learn quality control best practices, how to inspect products before shipping, and common QC mistakes to avoid.', img: 'https://readdy.ai/api/search-image?query=Quality%20inspection%20process%20checking%20clothing%20stitching%20and%20materials%20clean%20white%20background%20hands%20and%20magnifier%20minimalist%20editorial%20photography%20warm%20natural%20light%20professional&width=600&height=400&seq=pillar-04&orientation=landscape' },
    { title: 'AllChinaBuy Safety Guide', slug: 'allchinabuy-safety-guide', desc: 'Protect yourself from scams, verify seller legitimacy, secure your payments, and shop safely on international platforms.', img: 'https://readdy.ai/api/search-image?query=Digital%20security%20shield%20protecting%20online%20shopping%20concept%20minimalist%20illustration%20clean%20white%20background%20soft%20teal%20accents%20editorial%20style%20professional%20warm%20tones%20abstract&width=600&height=400&seq=pillar-05&orientation=landscape' },
    { title: 'AllChinaBuy Beginner Guide', slug: 'allchinabuy-beginner-guide', desc: 'New to AllChinaBuy? Start here with step-by-step instructions for your first order, account setup, and basic navigation.', img: 'https://readdy.ai/api/search-image?query=Beginner%20shopping%20guide%20concept%20with%20open%20laptop%20showing%20shopping%20website%20clean%20white%20desk%20warm%20natural%20light%20minimalist%20editorial%20photography%20professional%20soft%20shadows&width=600&height=400&seq=pillar-06&orientation=landscape' },
    { title: 'Is AllChinaBuy Legit?', slug: 'is-allchinabuy-legit', desc: 'We analyze platform legitimacy, user feedback, business practices, and what you should know before placing your first order.', img: 'https://readdy.ai/api/search-image?query=Trust%20verification%20concept%20checkmark%20badge%20certificate%20minimalist%20design%20clean%20white%20background%20warm%20lighting%20editorial%20photography%20professional%20soft%20shadows%20authenticity&width=600&height=400&seq=pillar-07&orientation=landscape' },
    { title: 'AllChinaBuy Coupon Guide', slug: 'allchinabuy-coupon-guide', desc: 'Find current discounts, understand how promotions work, and maximize your savings with verified coupon strategies.', img: 'https://readdy.ai/api/search-image?query=Discount%20coupon%20concept%20scissors%20cutting%20paper%20voucher%20minimalist%20style%20clean%20white%20background%20warm%20amber%20tones%20editorial%20photography%20professional%20soft%20shadows%20savings&width=600&height=400&seq=pillar-08&orientation=landscape' },
    { title: 'AllChinaBuy Warehouse Guide', slug: 'allchinabuy-warehouse-guide', desc: 'Understand warehouse storage processes, consolidation options, and how to manage your items before international shipping.', img: 'https://readdy.ai/api/search-image?query=Warehouse%20storage%20shelves%20with%20organized%20parcel%20boxes%20minimalist%20interior%20clean%20white%20lighting%20editorial%20photography%20warm%20neutral%20tones%20professional%20logistics%20arrangement&width=600&height=400&seq=pillar-09&orientation=landscape' },
    { title: 'AllChinaBuy vs Competitors', slug: 'allchinabuy-vs-competitors', desc: 'Compare AllChinaBuy with Superbuy, CSSBuy, and Sugargoo to find the best shopping agent for your specific needs.', img: 'https://readdy.ai/api/search-image?query=Comparison%20chart%20concept%20with%20balanced%20scales%20and%20multiple%20product%20boxes%20minimalist%20style%20clean%20white%20background%20warm%20neutral%20tones%20editorial%20photography%20professional%20soft%20shadows&width=600&height=400&seq=pillar-10&orientation=landscape' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main>
        {/* Hero Section - with solid background image instead of overlay */}
        <section className="relative w-full overflow-hidden bg-gray-900">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Premium%20fashion%20flat%20lay%20collection%20colorful%20sneakers%20designer%20hoodies%20luxury%20bags%20watches%20accessories%20arranged%20artistically%20dark%20charcoal%20background%20dramatic%20lighting%20editorial%20product%20photography%20high%20contrast%20professional%20ecommerce%20style&width=1600&height=900&seq=hero-fashion-2026&orientation=landscape"
              alt="Premium fashion collection flat lay"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50" />
          </div>

          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10 py-20 md:py-32">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
                AllChinaBuy Spreadsheet: Your Complete Buying & Shipping Resource
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-4 max-w-2xl mx-auto drop-shadow-md">
                Find, compare and understand AllChinaBuy products with one practical resource.
              </p>
              <p className="text-sm md:text-base text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
                The AllChinaBuy Spreadsheet brings together product research resources, seller information, QC guidance, shipping tips, buying tutorials and AllChinaBuy-related information in one easy-to-use hub.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <a
                  href={`/${lang}/article/allchinabuy-spreadsheet-complete-guide`}
                  className="px-8 py-3.5 bg-emerald-600 text-white rounded-lg font-medium text-sm hover:bg-emerald-700 transition-colors shadow-lg whitespace-nowrap"
                >
                  {t('cta_primary')}
                </a>
                <a
                  href={`/${lang}/article/allchinabuy-beginner-guide`}
                  className="px-8 py-3.5 bg-white/10 text-white border border-white/30 rounded-lg font-medium text-sm hover:bg-white/20 transition-colors backdrop-blur-sm whitespace-nowrap"
                >
                  {t('cta_secondary')}
                </a>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 border-2 border-white/60 text-white rounded-lg font-medium text-sm hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  allchinabuy spreadsheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 bg-white text-gray-900 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
                >
                  {t('cta_buy_now')}
                </a>
              </div>

              <p className="text-xs text-white/50">{t('trust_text')}</p>
            </div>
          </div>
        </section>

        {/* What Is Section */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6">
              {t('section_what_is_title')}
            </h2>
            <p className="text-base text-gray-600 text-center leading-relaxed max-w-2xl mx-auto mb-8">
              {t('section_what_is_desc')}
            </p>
            <div className="text-center">
              <a
                href={`/${lang}/article/allchinabuy-spreadsheet-complete-guide`}
                className="inline-flex items-center gap-2 text-emerald-600 font-medium text-sm hover:text-emerald-700 transition-colors"
              >
                Explore Spreadsheet Resources
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </section>

        {/* Product Carousels Section - NEW */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Browse Products by Category
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                Discover 100+ curated products across 14 categories. Click any item to view details and purchase on the main spreadsheet.
              </p>
            </div>
            <div className="space-y-12">
              {productsByCategory.map((cat) => (
                <ProductCarousel
                  key={cat.id}
                  categoryName={cat.name}
                  categoryUrl={cat.categoryUrl}
                  products={cat.products}
                />
              ))}
            </div>
            <div className="text-center mt-12">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-600 text-white rounded-lg font-medium text-sm hover:bg-primary-700 transition-colors shadow-lg whitespace-nowrap"
              >
                View Full Spreadsheet
                <i className="ri-external-link-line" />
              </a>
            </div>
          </div>
        </section>

        {/* Category Buttons Section */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-4">
              {t('section_categories_title')}
            </h2>
            <p className="text-sm text-gray-500 text-center mb-10 max-w-xl mx-auto">
              Browse product categories and jump directly to the main spreadsheet to find what you need.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex flex-col items-center justify-center p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all text-center"
                >
                  <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3 group-hover:bg-emerald-100 transition-colors">
                    <i className="ri-shopping-bag-line text-lg" />
                  </div>
                  <span className="text-sm font-medium text-gray-800 group-hover:text-emerald-700 transition-colors">
                    {cat.name}
                  </span>
                  <span className="text-xs text-emerald-600 mt-2 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    {t('category_button_label')} &rarr;
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Resources Cards with Images */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-24 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12">
              {t('section_resources_title')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {resourceCards.map((card, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={card.img}
                      alt={card.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <div className={`absolute bottom-3 left-3 w-10 h-10 rounded-lg ${card.color} flex items-center justify-center shadow-md`}>
                      <i className={`${card.icon} text-lg`} />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">{card.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compare Section */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {t('section_compare_title')}
                </h2>
                <p className="text-base text-gray-600 leading-relaxed mb-6">
                  {t('section_compare_desc')}
                </p>
                <a
                  href={`/${lang}/article/allchinabuy-vs-competitors`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap"
                >
                  {t('cta_view_comparisons')}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Side%20by%20side%20product%20comparison%20two%20sneakers%20on%20pedestals%20clean%20white%20studio%20background%20warm%20lighting%20editorial%20product%20photography%20professional%20style%20soft%20shadows%20minimalist&width=800&height=600&seq=compare-unique-01&orientation=landscape"
                  alt="Product comparison"
                  className="w-full h-64 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Shipping Section */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="relative rounded-xl overflow-hidden shadow-lg order-2 lg:order-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Airplane%20flying%20over%20world%20map%20with%20parcel%20delivery%20concept%20minimalist%20illustration%20clean%20white%20background%20warm%20amber%20tones%20editorial%20style%20professional%20soft%20shadows%20international%20shipping&width=800&height=600&seq=shipping-unique-02&orientation=landscape"
                  alt="International shipping"
                  className="w-full h-64 object-cover rounded-xl"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {t('section_shipping_title')}
                </h2>
                <p className="text-base text-gray-600 leading-relaxed mb-6">
                  {t('section_shipping_desc')}
                </p>
                <a
                  href={`/${lang}/article/allchinabuy-shipping-guide`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-lg font-medium text-sm hover:bg-amber-600 transition-colors whitespace-nowrap"
                >
                  {t('cta_explore_shipping')}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Safety Section */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {t('section_safety_title')}
                </h2>
                <p className="text-base text-gray-600 leading-relaxed mb-6">
                  {t('section_safety_desc')}
                </p>
                <a
                  href={`/${lang}/article/allchinabuy-safety-guide`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-medium text-sm hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  {t('cta_visit_safety')}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Secure%20digital%20shield%20with%20padlock%20and%20checkmark%20abstract%20minimalist%203D%20render%20clean%20white%20background%20soft%20teal%20gradients%20editorial%20illustration%20professional%20warm%20tones%20cybersecurity&width=800&height=600&seq=safety-unique-03&orientation=landscape"
                  alt="Digital safety concept"
                  className="w-full h-64 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 10 Pillar Articles Section */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-24 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-4">
              Pillar Articles & Resources
            </h2>
            <p className="text-sm text-gray-500 text-center mb-12 max-w-2xl mx-auto">
              In-depth guides covering every aspect of the AllChinaBuy experience — from your first order to advanced shipping optimization.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {pillarArticles.map((article, index) => (
                <a
                  key={index}
                  href={`/${lang}/article/${article.slug}`}
                  className="group block bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={article.img}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors mb-2 line-clamp-2 leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">
                      {article.desc}
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium mt-3 group-hover:gap-2 transition-all">
                      Read Guide
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 sm:px-6 lg:px-10 py-16 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12">
              {t('section_faq_title')}
            </h2>
            <FAQ />
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="relative w-full overflow-hidden bg-gray-900">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20fashion%20elements%20sneakers%20bags%20clothing%20fragments%20dark%20background%20teal%20and%20amber%20accent%20colors%20minimalist%20editorial%20style%20dramatic%20lighting%20professional%20texture&width=1600&height=900&seq=final-cta-bg&orientation=landscape"
              alt="Fashion pattern background"
              className="w-full h-full object-cover opacity-40"
            />
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10 py-20 md:py-28">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
                {t('final_cta_title')}
              </h2>
              <p className="text-base text-white/70 leading-relaxed mb-8 max-w-2xl mx-auto">
                {t('final_cta_desc')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={`/${lang}/article/allchinabuy-spreadsheet-complete-guide`}
                  className="px-8 py-3.5 bg-emerald-600 text-white rounded-lg font-medium text-sm hover:bg-emerald-700 transition-colors shadow-lg whitespace-nowrap"
                >
                  {t('cta_primary')}
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 border-2 border-white/50 text-white rounded-lg font-medium text-sm hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  Visit the Main Website
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}